'use client';

import { c as _c } from "react/compiler-runtime";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import type { FocusEventHandler, ReactNode } from 'react';
import { Checkbox } from '@src/components/Checkbox/Checkbox';
import { FieldCaption } from '@src/components/FieldCaption/FieldCaption';
import { cn } from '@src/utils/tailwind';
export interface DropdownItem<TId extends string> {
  id: TId;
  name: string | ReactNode;
  elementLeft?: ReactNode;
}
export interface Props<TId extends string> {
  items: DropdownItem<TId>[];
  selectedId?: TId;
  onItemIdSelected: (id: TId) => void;
  onBlur?: FocusEventHandler<HTMLButtonElement | HTMLDivElement>;
  placeholder?: string;
  listboxOptionsClassNames?: string;
  containerClassNames?: string;
  size?: 'small' | 'large';
  isInvalid?: boolean;
  captionText?: string;
  disabled?: boolean;
  showElementLeftInOptions?: boolean;
  listboxOptionsItemClassNames?: string;
  variant?: 'default' | 'dark';
  /** Dark variant only: render the selected-state checkbox on each option. */
  showOptionCheckbox?: boolean;
  'data-testid'?: string;
}
export function Dropdown(props) {
  const $ = _c(74);
  const {
    items,
    onItemIdSelected,
    onBlur,
    selectedId,
    placeholder,
    listboxOptionsClassNames,
    listboxOptionsItemClassNames,
    size: t0,
    containerClassNames,
    isInvalid,
    captionText,
    disabled,
    showElementLeftInOptions: t1,
    variant: t2,
    showOptionCheckbox: t3,
    "data-testid": dataTestId
  } = props;
  const size = t0 === undefined ? "small" : t0;
  const showElementLeftInOptions = t1 === undefined ? false : t1;
  const variant = t2 === undefined ? "default" : t2;
  const showOptionCheckbox = t3 === undefined ? true : t3;
  const isDark = variant === "dark";
  let t4;
  if ($[0] !== items || $[1] !== selectedId) {
    let t5;
    if ($[3] !== selectedId) {
      t5 = item => item.id === selectedId;
      $[3] = selectedId;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    t4 = items.find(t5);
    $[0] = items;
    $[1] = selectedId;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const itemForId = t4;
  const selected = placeholder && !itemForId ? undefined : itemForId ?? items?.[0];
  const t5 = !isDark;
  let t6;
  if ($[5] !== isDark || $[6] !== t5) {
    t6 = cn({
      "bg-white text-black": t5,
      "bg-transparent text-bodi-white": isDark
    });
    $[5] = isDark;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== selected) {
    t7 = selected ?? {
      id: "" as TId,
      name: ""
    } satisfies DropdownItem<TId>;
    $[8] = selected;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== onItemIdSelected) {
    t8 = e => {
      onItemIdSelected(e.id);
    };
    $[10] = onItemIdSelected;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = isDark ? "h-11 min-h-11 rounded-lg border-bodi-gray-medium bg-transparent px-[15px] py-0 text-bodi-gray-butters data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-bodi-gray-medium/25" : "rounded-[4px] border-bodi-gray-butters p-4 text-bodi-gray-dark data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-bodi-gray-butters/25";
  const t10 = !isDark && !selected?.name && placeholder;
  const t11 = !isDark && size === "small";
  const t12 = !isDark && size === "large";
  const t13 = !isDark && disabled;
  const t14 = isDark && disabled;
  let t15;
  if ($[12] !== containerClassNames || $[13] !== isInvalid || $[14] !== t10 || $[15] !== t11 || $[16] !== t12 || $[17] !== t13 || $[18] !== t14 || $[19] !== t9) {
    t15 = cn("relative flex w-full items-center justify-between border text-left text-body-base focus:outline-none", t9, containerClassNames, {
      "text-bodi-gray-medium": t10,
      "min-h-[45px]": t11,
      "min-h-[56px]": t12,
      "border-bodi-red-deep-500": isInvalid,
      "bg-bodi-gray-pearl text-bodi-gray-disabled": t13,
      "opacity-50": t14
    });
    $[12] = containerClassNames;
    $[13] = isInvalid;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t9;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = isDark ? "items-start" : "items-center gap-3";
  let t17;
  if ($[21] !== t16) {
    t17 = cn("flex", t16);
    $[21] = t16;
    $[22] = t17;
  } else {
    t17 = $[22];
  }
  const t18 = selected?.elementLeft;
  let t19;
  if ($[23] !== t18) {
    t19 = <div>{t18}</div>;
    $[23] = t18;
    $[24] = t19;
  } else {
    t19 = $[24];
  }
  const t20 = selected?.name ?? placeholder;
  let t21;
  if ($[25] !== t20) {
    t21 = <div>{t20}</div>;
    $[25] = t20;
    $[26] = t21;
  } else {
    t21 = $[26];
  }
  let t22;
  if ($[27] !== t17 || $[28] !== t19 || $[29] !== t21) {
    t22 = <div className={t17}>{t19}{t21}</div>;
    $[27] = t17;
    $[28] = t19;
    $[29] = t21;
    $[30] = t22;
  } else {
    t22 = $[30];
  }
  let t23;
  if ($[31] !== isDark) {
    t23 = isDark ? <ChevronUpDownIcon className="pointer-events-none size-5 shrink-0" aria-hidden="true" /> : <ChevronDownIcon className="pointer-events-none size-4" aria-hidden="true" />;
    $[31] = isDark;
    $[32] = t23;
  } else {
    t23 = $[32];
  }
  let t24;
  if ($[33] !== dataTestId || $[34] !== onBlur || $[35] !== t15 || $[36] !== t22 || $[37] !== t23) {
    t24 = <ListboxButton data-testid={dataTestId} onBlur={onBlur} className={t15}>{t22}{t23}</ListboxButton>;
    $[33] = dataTestId;
    $[34] = onBlur;
    $[35] = t15;
    $[36] = t22;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== listboxOptionsClassNames) {
    t25 = cn("mt-1 w-(--button-width) rounded-[4px] border border-bodi-gray-butters bg-white py-1.5 text-body-base shadow-lg [--anchor-gap:var(--spacing-1)] focus:outline-none", "transition duration-100 ease-in data-closed:opacity-0", listboxOptionsClassNames);
    $[39] = listboxOptionsClassNames;
    $[40] = t25;
  } else {
    t25 = $[40];
  }
  let t26;
  if ($[41] !== disabled || $[42] !== isDark || $[43] !== items || $[44] !== listboxOptionsItemClassNames || $[45] !== selected?.id || $[46] !== showElementLeftInOptions || $[47] !== showOptionCheckbox) {
    let t27;
    if ($[49] !== disabled || $[50] !== isDark || $[51] !== listboxOptionsItemClassNames || $[52] !== selected?.id || $[53] !== showElementLeftInOptions || $[54] !== showOptionCheckbox) {
      t27 = item_0 => <ListboxOption key={item_0.id} value={item_0} className={cn("group flex cursor-pointer gap-2 select-none hover:bg-bodi-accent-bright-300 data-focus:bg-bodi-accent-bright-300", isDark ? "h-9 items-center justify-between px-2.5 py-0 text-bodi-gray-dark" : "items-center px-3 py-1.5 text-bodi-black", listboxOptionsItemClassNames)}>{isDark ? <><div className="flex min-w-0 flex-1 items-center gap-2.5">{showElementLeftInOptions && <div>{item_0.elementLeft}</div>}<div className="text-body-base whitespace-nowrap">{item_0.name}</div></div>{showOptionCheckbox && <Checkbox checked={item_0.id === selected?.id} disabled={disabled} isDarkMode={true} onChange={_temp} tabIndex={-1} aria-hidden="true" className="pointer-events-none shrink-0 [&>div:last-child]:hidden" />}</> : <><CheckIcon className={cn("size-4 shrink-0 text-bodi-accent-deep-500", item_0.id !== selected?.id && "invisible")} aria-hidden="true" />{showElementLeftInOptions && <div>{item_0.elementLeft}</div>}<div className="text-body-base">{item_0.name}</div></>}</ListboxOption>;
      $[49] = disabled;
      $[50] = isDark;
      $[51] = listboxOptionsItemClassNames;
      $[52] = selected?.id;
      $[53] = showElementLeftInOptions;
      $[54] = showOptionCheckbox;
      $[55] = t27;
    } else {
      t27 = $[55];
    }
    t26 = items.map(t27);
    $[41] = disabled;
    $[42] = isDark;
    $[43] = items;
    $[44] = listboxOptionsItemClassNames;
    $[45] = selected?.id;
    $[46] = showElementLeftInOptions;
    $[47] = showOptionCheckbox;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[56] !== onBlur || $[57] !== t25 || $[58] !== t26) {
    t27 = <ListboxOptions anchor="bottom" onBlur={onBlur} transition={true} className={t25}>{t26}</ListboxOptions>;
    $[56] = onBlur;
    $[57] = t25;
    $[58] = t26;
    $[59] = t27;
  } else {
    t27 = $[59];
  }
  let t28;
  if ($[60] !== disabled || $[61] !== t24 || $[62] !== t27 || $[63] !== t7 || $[64] !== t8) {
    t28 = <Listbox value={t7} disabled={disabled} onChange={t8}>{t24}{t27}</Listbox>;
    $[60] = disabled;
    $[61] = t24;
    $[62] = t27;
    $[63] = t7;
    $[64] = t8;
    $[65] = t28;
  } else {
    t28 = $[65];
  }
  let t29;
  if ($[66] !== captionText || $[67] !== disabled || $[68] !== isInvalid) {
    t29 = captionText && <FieldCaption captionText={captionText} isInvalid={isInvalid} disabled={disabled} />;
    $[66] = captionText;
    $[67] = disabled;
    $[68] = isInvalid;
    $[69] = t29;
  } else {
    t29 = $[69];
  }
  let t30;
  if ($[70] !== t28 || $[71] !== t29 || $[72] !== t6) {
    t30 = <div className={t6}>{t28}{t29}</div>;
    $[70] = t28;
    $[71] = t29;
    $[72] = t6;
    $[73] = t30;
  } else {
    t30 = $[73];
  }
  return t30;
}
function _temp() {}